import React, { FC, useCallback, useMemo, useState, useEffect } from "react";

import { Placement } from "@popperjs/core";
import classNames from "classnames";
import { Moment } from "moment";
import moment from "moment-timezone";

import {
  StyledButton,
  StyledButtonGroup,
  StyledDiv,
  StyledSpan,
  StyledControl,
} from "./Styles";
import { DatePicker, TimestampDateRange } from "../../DatePicker/DatePicker";
import { ArrowRightIcon } from "../../Icons";
import ArrowLeftIcon from "../../Icons/collection/ArrowLeft";
import { InputMode } from "../../Input/types";

export interface IDateStepperProps {
  /** One of the possible steps for this component */
  step: "daily" | "weekly" | "monthly" | "quarterly" | "yearly" | "custom";
  /** Callback function that will be called after the changed range */
  onChange?: (from: Date, to: Date | null) => void;
  /** Start date of the range */
  from: Moment | string;
  /** End date of the range */
  to?: Moment | string;
  /** Callback function that will format the text label */
  formatCallback?: (from: string | Moment, to: string | Moment) => string;
  /** Default month */
  defaultMonth?: Moment;
  /** Callback function onBack */
  onBack?: (from: Date, to: Date) => void;
  /** Callback function onForward */
  onForward?: (from: Date, to: Date) => void;
  /** Min date */
  minValue?: Moment;
  /** Max fate */
  maxValue?: Moment;
  /** html class */
  className?: string;
  /** Period for calculating week (isoWeek) */
  period?: "week" | "isoWeek";
  /** Classes for datePicker */
  datePickerClass?: string;
  /** Show datePicker in the middle button */
  withDatePicker?: boolean;
  /** Popper class name*/
  popperClassName?: string;
  /** DatePicker menu position */
  position?: Placement;
  /** Enable year picker in DatePicker */
  enableYearPicker?: boolean;
  /** conditional border & button variant styles */
  mode?: InputMode;
  /** preserve date on smaller screens */
  alwaysShowDate?: boolean;
  /** Hide navigation arrows */
  disableArrows?: boolean;
  /**
   * Maximum number of days in a selection range (minimum value: 2, maximum value: 366).
   * @minimum 2
   * @maximum 366
   */
  maxRange?: number;
}

/**
 * This component allow you to select date range. It accepts two parameters "from" and "to" as valid format
 * onChange and onBack and onForward it will return "from: Date" and "to: Date" as function arguments
 */

export const DateStepper: FC<IDateStepperProps> = ({
  step,
  formatCallback,
  onChange,
  from,
  to,
  onBack,
  onForward,
  minValue,
  maxValue,
  className,
  period = "week",
  withDatePicker = true,
  defaultMonth = moment(),
  enableYearPicker,
  position = "bottom",
  popperClassName,
  mode = "outlined",
  alwaysShowDate = false,
  disableArrows = false,
  maxRange,
}) => {
  const [fromDate, setFromDate] = useState<Moment>(moment.utc(from));
  const [toDate, setToDate] = useState<Moment>(to ? moment.utc(to) : null);

  const getDatesByPeriod = useCallback(
    (interval: number): Moment[] => {
      const date = fromDate ? fromDate : moment();
      if (step === "daily") {
        date.add(interval, "days");
        return [date, date.clone()];
      } else if (step === "weekly") {
        date.add(interval, "weeks");
        return [date.clone().startOf(period), date.clone().endOf(period)];
      } else if (step === "monthly") {
        date.add(interval, "months");
        return [date.clone().startOf("month"), date.clone().endOf("month")];
      } else if (step === "quarterly") {
        date.add(interval, "quarters");
        return [date.clone().startOf("quarter"), date.clone().endOf("quarter")];
      } else if (step === "yearly") {
        date.add(interval, "years");
        return [date.clone().startOf("year"), date.clone().endOf("year")];
      } else if (step === "custom") {
        // For range step, calculate N days based on current selection
        // Handle case where toDate is undefined
        const effectiveToDate = !toDate ? fromDate : toDate;

        const daysDiff = effectiveToDate.diff(fromDate, "days");

        // If same day selected (daysDiff = 0), move by 1 day
        const daysToMove = daysDiff === 0 ? 1 : daysDiff + 1;
        const newFromDate = fromDate.clone().add(interval * daysToMove, "days");
        const newToDate =
          daysDiff === 0
            ? newFromDate.clone()
            : effectiveToDate.clone().add(interval * daysToMove, "days");

        return [newFromDate, newToDate];
      }
      return [date, date];
    },
    [fromDate, toDate, period, step]
  );

  const getDateFormatted = useMemo(() => {
    if (formatCallback) {
      return formatCallback(fromDate, toDate ? toDate : fromDate);
    }

    const actualYear = moment().year();

    const showWeekOrCustomDateFormat = (start, end): string => {
      const endDate = end ? end : start;
      if (start.year() === actualYear && end.year() === actualYear) {
        return (
          start.utc().format("MMM DD") + " - " + endDate.utc().format("MMM DD")
        );
      } else {
        return (
          start.utc().format("MMM DD YYYY") +
          " - " +
          endDate.utc().format("MMM DD YYYY")
        );
      }
    };

    if (step === "daily") {
      if (fromDate.year() === actualYear) {
        return fromDate.utc().format("MMM DD");
      }
      return fromDate.utc().format("MMM DD YYYY");
    } else if (step === "weekly") {
      const startDate = fromDate.clone().utc().startOf(period);
      const endDate = startDate.clone().utc().endOf(period);
      return showWeekOrCustomDateFormat(startDate, endDate);
    } else if (step === "monthly") {
      return fromDate.utc().format("MMM YYYY");
    } else if (step === "quarterly") {
      return `Q${fromDate.utc().quarter()}/${fromDate.utc().format("YYYY")}`;
    } else if (step === "yearly") {
      return fromDate.utc().format("YYYY");
    } else if (step === "custom") {
      if (!toDate) {
        if (fromDate.year() === actualYear) {
          return fromDate.utc().format("MMM DD");
        }
        return fromDate.utc().format("MMM DD YYYY");
      }
      // If from and to are the same date, show only once
      if (fromDate.format("YYYY-MM-DD") === toDate.format("YYYY-MM-DD")) {
        if (fromDate.year() === actualYear) {
          return fromDate.utc().format("MMM DD");
        }
        return fromDate.utc().format("MMM DD YYYY");
      }
      return showWeekOrCustomDateFormat(fromDate, toDate);
    }

    return "";
  }, [fromDate, toDate, step, formatCallback, period]);

  const datePickerMode = useMemo(() => {
    if (step === "custom") return "custom";
    if (step === "yearly") return "custom";
    return step;
  }, [step]);

  const onDatePickerChange = useCallback(
    (dates?: TimestampDateRange) => {
      console.log("dates: ", dates);
      const fromDateTemp: Moment = moment.utc(Number(dates?.from) * 1000);
      const toDateTemp: Moment = dates?.to
        ? moment.utc(Number(dates?.to) * 1000)
        : null;

      setFromDate(fromDateTemp);
      setToDate(toDateTemp);

      if (
        onChange &&
        step !== "custom" &&
        moment.isMoment(fromDateTemp) &&
        moment.isMoment(toDateTemp)
      ) {
        onChange(fromDateTemp.toDate(), toDateTemp.toDate());
      }
    },
    [onChange, step]
  );

  const onToggleDatePicker = useCallback(
    (open?: boolean) => {
      if (step === "custom" && !open) {
        const toDateTemp: Moment = toDate ? toDate : fromDate;
        setToDate(toDateTemp);
        onChange?.(fromDate.toDate(), toDateTemp.toDate());
      }
    },
    [fromDate, onChange, step, toDate]
  );

  const isAfterMaxDate = useMemo(() => {
    return (
      moment.isMoment(maxValue) &&
      toDate &&
      moment(moment.utc(maxValue).format("YYYY-MM-DD")).isSameOrBefore(
        toDate.utc().format("YYYY-MM-DD")
      )
    );
  }, [maxValue, toDate]);

  const isBeforeMinDate = useMemo(() => {
    return (
      moment.isMoment(minValue) &&
      toDate &&
      moment
        .utc(moment.utc(minValue).format("YYYY-MM-DD"))
        .isSameOrAfter(toDate.utc().format("YYYY-MM-DD"))
    );
  }, [minValue, toDate]);

  useEffect(() => {
    if (isBeforeMinDate) {
      if (step === "daily") {
        setFromDate(moment.utc(minValue));
        setToDate(moment.utc(minValue));
      }
    }
    if (isAfterMaxDate) {
      if (step === "daily") {
        setFromDate(moment.utc(maxValue));
        setToDate(moment.utc(maxValue));
      }
    }
  }, [step, minValue, maxValue, isBeforeMinDate, isAfterMaxDate]);

  const onRightClickHandler = useCallback(() => {
    if (isAfterMaxDate) {
      return;
    }

    const [newFrom, newTo] = getDatesByPeriod(1);
    setFromDate(moment.utc(newFrom));
    setToDate(moment.utc(newTo));

    if (onChange) {
      onChange(moment.utc(newFrom).toDate(), moment.utc(newTo).toDate());
    }

    if (onForward) {
      onForward(moment.utc(newFrom).toDate(), moment.utc(newTo).toDate());
    }
  }, [isAfterMaxDate, getDatesByPeriod, onChange, onForward]);

  const onLeftClickHandler = useCallback(() => {
    if (isBeforeMinDate) {
      return;
    }

    const [newFrom, newTo] = getDatesByPeriod(-1);
    setFromDate(moment.utc(newFrom));
    setToDate(moment.utc(newTo));

    if (onChange) {
      onChange(moment.utc(newFrom).toDate(), moment.utc(newTo).toDate());
    }
    if (onBack) {
      onBack(moment.utc(newFrom).toDate(), moment.utc(newTo).toDate());
    }
  }, [isBeforeMinDate, getDatesByPeriod, onChange, onBack]);

  useEffect(() => {
    setFromDate(moment.utc(from));
    setToDate(to ? moment.utc(to) : null);
  }, [from, to]);

  const dateStepperWidth = (
    fromDay: Moment,
    toDay: Moment | undefined,
    range: string
  ): { minWidth: string } => {
    if (range === "range") {
      return { minWidth: "102px" };
    }
    if (
      fromDay.utc().year() !== moment.utc().year() ||
      (!!toDay && toDay?.utc().year() !== moment.utc().year())
    ) {
      switch (range) {
        case "daily":
        case "monthly":
          return { minWidth: "122px" };
        case "weekly":
        case "custom":
          return { minWidth: "232px" };
      }
    }
    switch (range) {
      case "monthly":
        return { minWidth: "122px" };
      case "weekly":
      case "custom":
        return { minWidth: "182px" };
    }

    return { minWidth: "102px" };
  };
  const isDisabled = useCallback(
    (day: Moment): boolean => {
      const dayFormat = moment.utc(day).format("YYYY-MM-DD");

      const isBefore =
        moment.isMoment(maxValue) &&
        moment(moment.utc(maxValue).format("YYYY-MM-DD")).isBefore(dayFormat);
      const isAfter =
        moment.isMoment(minValue) &&
        moment(moment.utc(minValue).format("YYYY-MM-DD")).isAfter(dayFormat);

      return isBefore || isAfter;
    },
    [maxValue, minValue]
  );

  const modifiers = useMemo(
    () => ({
      disabled: (day: Moment) => ({ matched: isDisabled(day), title: null }),
      nonWorkingDay: (day: Moment) => ({
        matched: isDisabled(day),
        title: null,
      }),
    }),
    [isDisabled]
  );

  return (
    <StyledButtonGroup className={classNames("c-date-stepper", className)}>
      {!disableArrows ? (
        <StyledControl
          type="button"
          variant={mode === "flat" ? "text gray" : "secondary"}
          onClick={onLeftClickHandler}
          disabled={isBeforeMinDate}
        >
          <ArrowLeftIcon />
        </StyledControl>
      ) : null}
      <StyledDiv
        $isTargetable={step === "yearly" || !withDatePicker}
        $isRounded={disableArrows}
        $mode={mode}
        $alwaysShowDate={alwaysShowDate}
        style={dateStepperWidth(fromDate, toDate, step)}
      >
        {withDatePicker && step !== "yearly" ? (
          <DatePicker
            month={defaultMonth}
            onCalendarToggle={onToggleDatePicker}
            target={
              <StyledButton $step={step} type="button">
                {getDateFormatted}
              </StyledButton>
            }
            mode={datePickerMode}
            selected={{
              from: moment.utc(fromDate).unix(),
              to: toDate ? moment.utc(toDate).unix() : null,
            }}
            onChange={onDatePickerChange}
            firstDayOfWeek={period === "week" ? 0 : 1}
            modifiers={modifiers}
            disabledDaysAfter={maxValue}
            disabledDaysBefore={minValue}
            disableYearPicker={!enableYearPicker}
            popperClassName={popperClassName}
            position={position}
            maxRange={maxRange}
            key={datePickerMode}
            instant
            required
          />
        ) : (
          <StyledSpan>{getDateFormatted}</StyledSpan>
        )}
      </StyledDiv>
      {!disableArrows ? (
        <StyledControl
          type="button"
          variant={mode === "flat" ? "text gray" : "secondary"}
          onClick={onRightClickHandler}
          disabled={isAfterMaxDate}
        >
          <ArrowRightIcon />
        </StyledControl>
      ) : null}
    </StyledButtonGroup>
  );
};

DateStepper.displayName = "DateStepper";
